import type {Dayjs} from "dayjs";

export enum PostScope {
    PUBLIC = 0,
    SUBSCRIBERS_ONLY = 20
}

export enum AttachmentType {
    UNKNOWN = -1,
    POST = 0,
    POLL = 1,
    STREAM = 2,
    EXTERNAL_LINK = 3,
}

export enum ContentFlag {
    adult = 'adult',
    drugs_and_alcohol = 'drugs_and_alcohol',
    sexual = 'sexual',
    violence = 'violence',
}

export enum ContentPreferenceValue {
    HIDE = -1,
    BLUR = 0,
    SHOW = 1
}

export enum MediaType {
    UNKNOWN = -1,
    IMAGE = 0,
    VIDEO = 1,
    AUDIO = 2,
    DOC = 3,
    AUDIO_BOOKS = 4,
    BOOKS = 5
}

export enum ExternalLinkType {
    Link = 0,
    Embedded_Youtube = 1,
    Embedded_Vimeo = 2,
    Embedded_Twitter = 3,
}

export enum ChatType {
    IM = 0,
    GROUP_CHAT = 1,
    PRIVATE_CHANNEL = 2
}

export enum ChatMessageType {
    MESSAGE = 0,
    TIP = 1
}

export type DataWrapped<T> = {
    data: T
}

export type HasId = {
    id: number
}

export type HasStringableId = {
    id: string
}

export type HasUuid = { uuid: string };

export type PriceModel = {
    amount: number
    currency_code: string //@todo ENUM
}

export type Post = HasId & {
    message: string
    expires: string
    price: PriceModel,
    media: Array<Media>
    published_at: string
    author: Author
    likes_count: number
    comments_count: number
    reposts_count: number
    is_pinned: boolean
    reposted_by: UserMinimal | null
    is_blurred: boolean
    is_liked: boolean
    is_first_post: boolean
    is_repost: boolean
    is_owner: boolean
    has_access: boolean
    attachments: Array<Attachment<Post | ExternalLink>>
    content_flags: Array<ContentFlag>
    scope: PostScope,
    source_post_id: number | undefined
}

export type PollOption = {
    label: string | number,
    users_choice: true | undefined
    votes: number
}

export type Poll = HasId & {
    has_voted: boolean
    question: string,
    options: Array<PollOption>
    total_votes: number
}

export type FeedPost = Post & {
    is_liked: boolean
}

export type Media = {
    has_access: boolean,
    size: number
    type: MediaType
    // uploader: Author
    url: string
    uuid: string
}

export type UserMinimal = HasId & {
    name: string
    username: string
    avatar: string;
    is_online: boolean
    initials: string
}

export type UserStats = {
    images: number
    video: number
    likes: number
    posts: number
    followers: number
    following: number
}

export type User = UserMinimal & {
    bio: string;
    stats: UserStats
    is_adult: boolean

    is_followed: boolean
    is_following: boolean
    is_verified: boolean
    is_age_verified: boolean
}

export type AuthenticatedUser = User & {
    settings: {
        years: number
        locale: string,
        content_preferences: Record<ContentFlag, ContentPreferenceValue>
    }
}

export type Author = User & {
    referral_code: string
    subscription: ProfileSubscriptionInfo
}

export type ProfileSubscriptionInfo = {
    benefits: Array<string>
    is_subscribed: boolean
    was_subscribed: boolean
    monthly: number
    yearly: number
    currency_code: string
}

export type Profile = Author & {
    social_accounts: Array<SocialAccount>
    is_owner: boolean,
    is_creator: boolean
}

export type SocialAccount = {
    created_at: string;
    id: number;
    name: string;
    provider: "instagram"
    updated_at: string;
    user_id: number;
    href: string
}

export type ExternalLink = {
    created_at: string
    id: number
    meta: {
        description: string
        image: string
        title: string
        url: string
    }
    type: ExternalLinkType
    updated_at: string
    url: string
}

export type Attachment<T> = {
    id: number
    attached: T,
    type: AttachmentType
}

export type PaginatedDataResult<T> = {
    data: Array<T>,
    links: {
        first: string,
        last: string,
        prev: string | null,
        next: string | null
    }
    meta: {
        current_page: number,
        from: number,
        last_page: number,
        path: string
        per_page: number
        to: number
        total: number
    }
}

export type ChatMessage = HasId & {
    attachments: Array<Attachment<ExternalLink>>
    created_at: Dayjs
    from: Author
    has_access: boolean
    items: Array<any>
    likes_count: number
    media: Array<Media>
    message: string
    read: boolean
    type: ChatMessageType

    dayRelated: string
}

export type Chat = HasStringableId & {
    created_at: Dayjs
    member?: UserMinimal | undefined
    author?: UserMinimal | undefined
    message?: ChatMessage | null
    name: string
    read: boolean
    type: ChatType
}

export type Comment = HasId & {
    parent_id: number,
    lvl: number,
    message: string,
    created_at: Dayjs,
    user: Author,
    likes_count: number | undefined,
    children: Array<Comment>,
    children_count: number,
    is_liked: boolean,
}

export type NotificationsFilter = 'all' | 'followings' | 'comments' | 'likes';